/* eslint-disable complexity */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Flex, Text, Link
} from 'components/ui';
import SDUBanner from 'components/ProductPage/DeliveryOptions/SDUBanner';
import SDUBannerNDD from 'components/ProductPage/DeliveryOptions/SDUBannerNDD';
import SDULandingPageModal from 'components/GlobalModals/SDULandingPageModal';
import InfoButton from 'components/InfoButton/InfoButton';
import Markdown from 'components/Markdown/Markdown';
import { renderModal, globalModals } from 'utils/globalModals';
import helpersUtils from 'utils/Helpers';
import Location from 'utils/Location';
import DeliveryOptionsUtils from 'utils/DeliveryOptions';
import Actions from 'actions/Actions';
import PreferredZipCodeSelector from 'selectors/user/preferredZipCodeSelector';
import UserSubscriptionsSelector from 'selectors/user/userSubscriptions/userSubscriptionsSelector';
import basketSelector from 'selectors/basket/basketSelector';
import { globalModalsSelector } from 'selectors/page/headerFooterTemplate/data/globalModals/globalModalsSelector';
import ExtraProductDetailsUtils from 'utils/ExtraProductDetailsUtils';
import userUtils from 'utils/User';
import localeUtils from 'utils/LanguageLocale';
import BCCUtils from 'utils/BCC';
import stringUtils from 'utils/String';
import SDDRougeTestV2InfoModal from 'utils/SDDRougeTestV2InfoModal';
import basketUtils from 'utils/Basket';
import EN from 'components/ProductPage/DeliveryOptions/locales/DeliveryOptions_en_US';
import FR from 'components/ProductPage/DeliveryOptions/locales/DeliveryOptions_fr_CA';

const { replaceDoubleAsterisks } = helpersUtils;
const { getSDDCountdownMessage, isSDUAllowedForUser, isNDDNewDeliveryName } = DeliveryOptionsUtils;
const { SDD_FULFILLMENT_SERVICE_INFO } = globalModals;
const { MEDIA_IDS } = BCCUtils;
const { SAME_DAY_UNLIMITED_MODAL_US, SAME_DAY_UNLIMITED_MODAL_CA } = MEDIA_IDS;
const { preferredZipCodeSelector } = PreferredZipCodeSelector;
const { userSubscriptionsSelector } = UserSubscriptionsSelector;
const getText = localeUtils.getLocalisedDataFromResource(EN, FR);

function SameDayDelivery({
    currentProduct,
    sameDayNotAvailableForZip,
    sameDayAvailable,
    sameDayDelivery,
    sameDayDeliveryClick,
    serviceUnavailable,
    displayOrderCutoffCountdown,
    toggleSDULandingPage,
    renderSDULandingPage,
    isSDUAddedToBasket,
    skuTrialEligibility,
    skuTrialPeriod,
    isUserSDUTrialEligible,
    showBCCMediaModal,
    bccMediaSpecInfo,
    sameDayDeliveryMessageCountdown
}) {
    const dispatch = useDispatch();
    const preferredZipCode = useSelector(preferredZipCodeSelector);
    const userSubscriptions = useSelector(userSubscriptionsSelector);
    const basket = useSelector(basketSelector);
    const globalModalsData = useSelector(globalModalsSelector);

    const showShippingDeliveryLocationModal = useCallback(opts => dispatch(Actions.showShippingDeliveryLocationModal(opts)), [dispatch]);

    const { currentSku = {}, SDDRougeTestRemainToFreeShipping, SDDRougeTestThreshold, SDDRougeTestFreeShipping } = currentProduct;
    const actionFlags = currentSku?.actionFlags;
    const isSameDayEligibleSku = currentSku?.isSameDayEligibleSku;
    const sameDayAvailabilityStatus = actionFlags?.sameDayAvailabilityStatus;
    const isOutOfStock = ExtraProductDetailsUtils.isOutOfStock(sameDayAvailabilityStatus);

    // Static locale strings
    const forText = getText('for');
    const yourLocationText = getText('yourLocation');
    const changeLocationBaseText = getText('changeLocation');
    const notAvailableText = isNDDNewDeliveryName() ? getText('fastDeliveryNotAvailableForZip') : getText('notAvailable');
    const aboutSameDayDeliveryText = isNDDNewDeliveryName() ? getText('aboutFastDelivery') : getText('aboutSameDayDelivery');
    const sddTemporarilyUnavailableAtLocationText = isNDDNewDeliveryName()
        ? getText('fastDeliveryTemporarilyUnavailableAtLocation')
        : getText('sddTemporarilyUnavailableAtLocation');
    const sameDayDeliveryNotAvailableText = isNDDNewDeliveryName() ? getText('fastDeliveryNotAvailable') : getText('sameDayDeliveryNotAvailable');
    const sddRougeFreeShipMessageText = isNDDNewDeliveryName() ? getText('fastDeliveryRougeFreeShipMessage') : getText('sddRougeFreeShipMessage');

    // Derived values (preserving HOC combiner logic verbatim)
    const locationText = preferredZipCode || yourLocationText;
    const changeLocationText = (isOutOfStock || sameDayNotAvailableForZip) && changeLocationBaseText;
    const availabilityLabel = sameDayAvailabilityStatus
        ? ExtraProductDetailsUtils.availabilityLabel(sameDayAvailabilityStatus)
        : 'selectForStoreAvailability';
    const availabilityText = sameDayNotAvailableForZip ? notAvailableText : getText(availabilityLabel);
    const sameDayDeliveryMessage =
        isOutOfStock || !currentSku?.sameDayDeliveryMessage
            ? null
            : displayOrderCutoffCountdown
                ? getSDDCountdownMessage(currentSku?.sameDayDeliveryMessage)
                : currentSku?.sameDayDeliveryMessage;
    const aboutSameDayDeliveryLink = aboutSameDayDeliveryText;
    const sameDayUnavailableMessage = serviceUnavailable
        ? sddTemporarilyUnavailableAtLocationText
        : !isSameDayEligibleSku && sameDayAvailable
            ? sameDayDeliveryNotAvailableText
            : null;
    const availabilityTextColor = !sameDayAvailabilityStatus ? null : !isSameDayEligibleSku ? 'gray' : isOutOfStock ? 'red' : 'green';
    const sddRougeFreeShipMessage = userUtils.isSDDRougeFreeShipEligible() && isSameDayEligibleSku && sddRougeFreeShipMessageText;

    const isSDDRougeTestV2 = SDDRougeTestThreshold;
    const currentSDDRougeTestRemainToFreeShipping =
        SDDRougeTestRemainToFreeShipping !== basket.SDDRougeTestRemainToFreeShipping
            ? basket.SDDRougeTestRemainToFreeShipping
            : SDDRougeTestRemainToFreeShipping;
    const currentSDDRougeTestFreeShipping =
        SDDRougeTestFreeShipping !== basket.SDDRougeTestFreeShipping ? basket.SDDRougeTestFreeShipping : SDDRougeTestFreeShipping;
    const sddRougeTestV2FreeShippingMessage =
        isSDDRougeTestV2 &&
        (currentSDDRougeTestRemainToFreeShipping && !currentSDDRougeTestFreeShipping
            ? stringUtils.format(
                isNDDNewDeliveryName() ? getText('fastDeliveryRougeTestV2Message', ['{0}']) : getText('sddRougeTestV2Message', ['{0}']),
                currentSDDRougeTestRemainToFreeShipping
            )
            : isNDDNewDeliveryName()
                ? getText('fastDeliveryRougeTestV2FreeShippingMessage')
                : getText('sddRougeTestV2FreeShippingMessage'));
    const showSDDRougeTestV2InfoModal = () => SDDRougeTestV2InfoModal.showModal(toggleSDULandingPage, SDDRougeTestThreshold);
    const sameDayItems = basketUtils.getSameDayItems(basket);
    const hasSameDayItems = basketUtils.hasSameDayItems();
    const sddRougeV2SameDayDeliveryMessage = hasSameDayItems && sameDayItems.sameDayDeliveryMessage;
    const displayedSameDayDeliveryMessage =
        isSDDRougeTestV2 && sddRougeV2SameDayDeliveryMessage && sameDayDelivery ? sddRougeV2SameDayDeliveryMessage : sameDayDeliveryMessage;

    const isUserSDUTrialAllowed = isSDUAllowedForUser(userSubscriptions);
    const isCanada = localeUtils.isCanada();
    const sduMediaId = isCanada ? SAME_DAY_UNLIMITED_MODAL_CA : SAME_DAY_UNLIMITED_MODAL_US;
    const sddFulFillmentServiceInfoModal = globalModalsData[SDD_FULFILLMENT_SERVICE_INFO];

    const entry = Location.isProductPage() ? 'ppage sdd' : '';
    const isNDDEnabled = DeliveryOptionsUtils.isNDDEnabled();

    return (
        <>
            {sameDayUnavailableMessage && (
                <Text
                    is='p'
                    marginBottom={1}
                    color='gray'
                    children={sameDayUnavailableMessage}
                />
            )}

            <Text
                is='p'
                marginBottom='2px'
            >
                <Text
                    data-at={Sephora.debug.dataAt('pdp_see_availability_label')}
                    fontWeight={sameDayAvailabilityStatus && 'bold'}
                    color={availabilityTextColor}
                    children={availabilityText}
                />
                {` ${forText} `}
                <Link
                    onClick={() =>
                        showShippingDeliveryLocationModal({
                            isOpen: true,
                            entry,
                            callback: ({ sameDayAvailable: sda }) => {
                                sameDayDeliveryClick(sda, true);
                            },
                            sduZipcodeModal: false,
                            sku: currentProduct?.currentSku
                        })
                    }
                    arrowDirection='down'
                    padding={2}
                    margin={-2}
                    fontWeight='bold'
                    children={locationText}
                ></Link>
            </Text>
            <Flex
                flexDirection='column'
                gap={2}
            >
                {changeLocationText && (
                    <p>
                        <Link
                            color='blue'
                            onClick={() =>
                                showShippingDeliveryLocationModal({
                                    isOpen: true,
                                    callback: ({ sameDayAvailable: sda }) => {
                                        sameDayDeliveryClick(sda, true);
                                    },
                                    sduZipcodeModal: false
                                })
                            }
                            padding={2}
                            margin={-2}
                            children={changeLocationText}
                        />
                    </p>
                )}
                {displayedSameDayDeliveryMessage && (
                    <Text
                        is='p'
                        color='green'
                        fontSize='sm'
                    >
                        <Markdown
                            is='span'
                            css={styles.sddMessageMarkdown}
                            content={replaceDoubleAsterisks(sameDayDeliveryMessageCountdown || displayedSameDayDeliveryMessage)}
                        />
                    </Text>
                )}
                {sddRougeFreeShipMessage && (
                    <Box
                        is='p'
                        backgroundColor='white'
                        paddingX={3}
                        paddingY={2}
                        borderWidth={1}
                        borderColor='lightGray'
                        borderRadius={2}
                        children={sddRougeFreeShipMessage}
                    />
                )}
                {sddRougeTestV2FreeShippingMessage && !isSDUAddedToBasket ? (
                    <Box
                        is='p'
                        backgroundColor='white'
                        paddingX={3}
                        paddingY={2}
                        borderWidth={1}
                        borderColor='lightGray'
                        borderRadius={2}
                    >
                        <Text
                            marginRight={1}
                            children={sddRougeTestV2FreeShippingMessage}
                        />
                        <InfoButton
                            size={16}
                            onClick={showSDDRougeTestV2InfoModal}
                        />
                    </Box>
                ) : !isNDDEnabled ? (
                    <SDUBanner
                        isSDUAddedToBasket={isSDUAddedToBasket}
                        skuTrialEligibility={skuTrialEligibility}
                        skuTrialPeriod={skuTrialPeriod}
                        renderSDULandingPage={toggleSDULandingPage}
                    />
                ) : null}
                {renderSDULandingPage && (
                    <SDULandingPageModal
                        isOpen={renderSDULandingPage}
                        onDismiss={toggleSDULandingPage}
                        mediaId={sduMediaId}
                        skuTrialPeriod={skuTrialPeriod}
                        isSDUAddedToBasket={isSDUAddedToBasket}
                        isUserSDUTrialEligible={isUserSDUTrialEligible}
                        isCanada={isCanada}
                        skipConfirmationModal={false}
                        isUserSDUTrialAllowed={isUserSDUTrialAllowed}
                    />
                )}
                {aboutSameDayDeliveryLink && (
                    <p>
                        <Link
                            color='blue'
                            onClick={e =>
                                renderModal(sddFulFillmentServiceInfoModal, () => {
                                    showBCCMediaModal(e, {
                                        type: 'information',
                                        info: bccMediaSpecInfo
                                    });
                                })
                            }
                            padding={2}
                            margin={-2}
                            children={aboutSameDayDeliveryLink}
                        />
                    </p>
                )}
            </Flex>
            {isNDDEnabled && !(sddRougeTestV2FreeShippingMessage && !isSDUAddedToBasket) && (
                <SDUBannerNDD
                    skuTrialEligibility={skuTrialEligibility}
                    renderSDULandingPage={toggleSDULandingPage}
                />
            )}
        </>
    );
}

const styles = {
    sddMessageMarkdown: {
        '& > :first-child': {
            display: 'inline'
        }
    }
};

SameDayDelivery.propTypes = {
    currentProduct: PropTypes.object,
    sameDayNotAvailableForZip: PropTypes.bool,
    sameDayAvailable: PropTypes.bool,
    sameDayDelivery: PropTypes.bool,
    sameDayDeliveryClick: PropTypes.func,
    serviceUnavailable: PropTypes.bool,
    displayOrderCutoffCountdown: PropTypes.bool,
    toggleSDULandingPage: PropTypes.func,
    renderSDULandingPage: PropTypes.bool,
    isSDUAddedToBasket: PropTypes.bool,
    skuTrialEligibility: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    skuTrialPeriod: PropTypes.string,
    isUserSDUTrialEligible: PropTypes.bool,
    showBCCMediaModal: PropTypes.func,
    bccMediaSpecInfo: PropTypes.string,
    sameDayDeliveryMessageCountdown: PropTypes.string
};

export default wrapFunctionalComponent(SameDayDelivery, 'SameDayDelivery');
